import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { api, ApiError } from "@/lib/api";
import { isModerationDecision, useModeration } from "@/lib/moderation";
import type { GenerateRequest, GenerateResponse, GenerationStatusResponse } from "@/types";

const SESSION_KEY = "animepup_active_generations";

function parseModerationResult(value: unknown) {
  if (!isModerationDecision(value)) {
    throw new Error("We couldn’t read the moderation result. Please try again shortly.");
  }
  return value;
}

export function registerGenerationId(id: string) {
  try {
    const ids: string[] = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]");
    if (!ids.includes(id)) ids.push(id);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(ids));
  } catch {}
}

export function isGenerationAllowed(id: string): boolean {
  try {
    const ids: string[] = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]");
    return ids.includes(id);
  } catch {
    return false;
  }
}

export function useGenerate() {
  const { user } = useUser();
  const { showModerationResult } = useModeration();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Omit<GenerateRequest, "user_id">) => {
      if (!user?.id) throw new Error("No user ID");
      let data: GenerateResponse;
      try {
        data = await api.post<GenerateResponse>("/generate", {
          ...params,
          user_id: user.id,
        });
      } catch (error) {
        // 422 and 503 responses carry the gateway's moderation decision.
        if (error instanceof ApiError && error.moderation !== undefined) {
          const result = parseModerationResult(error.moderation);
          showModerationResult(result);
        }
        throw error;
      }
      // Job creation already succeeded; optional popup data must not hide the job.
      if (isModerationDecision(data.moderation)) showModerationResult(data.moderation);
      return data;
    },
    onSuccess: (data) => {
      if (data?.video?.id) registerGenerationId(data.video.id);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useGenerationStatus(generationId: string | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["generation", generationId],
    queryFn: async () => {
      if (!generationId) throw new Error("No generation ID");
      return api.get<GenerationStatusResponse>(
        `/generation/${generationId}/status`,
      );
    },
    enabled: !!generationId,
    refetchInterval: (query) => {
      const status = query.state.data?.generation?.status;
      if (status === "completed" || status === "failed") {
        queryClient.invalidateQueries({ queryKey: ["videos"] });
        queryClient.invalidateQueries({ queryKey: ["home-recent-videos"] });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        return false;
      }
      return 3000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });
}
